import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, Package, DollarSign, Boxes, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "", image: "", category: "Pokemon", rarity: "Rare", grade: "", stock: "", featured: false };
const CATEGORIES = ["Pokemon", "Sealed", "Basketball", "Football", "Baseball", "Hockey", "Sports"];
const RARITIES = ["Common", "Uncommon", "Rare", "Ultra Rare", "Secret Rare", "Sealed"];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) nav("/login");
    if (user && user.role !== "admin") nav("/");
  }, [user, nav]);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({ ...p, price: String(p.price), stock: String(p.stock) });
    setEditId(p.id); setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0 };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Card updated" : "Card added");
      setShowForm(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this card?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Card deleted"); load();
  };

  const revenue = orders.reduce((s, o) => s + (o.amount || 0), 0);
  const stats = [
    { icon: Package, label: "Products", value: products.length },
    { icon: Boxes, label: "Orders", value: orders.length },
    { icon: DollarSign, label: "Revenue", value: `$${revenue.toFixed(2)}` },
  ];

  if (!user || user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline text-secondary">Control Room</p>
          <h1 className="mt-2 font-display text-3xl font-900 tracking-tighter text-white sm:text-4xl">Admin Dashboard</h1>
        </div>
        <button onClick={openCreate} data-testid="add-product-btn"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 font-medium text-white transition-transform hover:-translate-y-0.5">
          <Plus size={18} /> Add card
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-white/10 bg-card p-6">
            <s.icon className="text-secondary" size={22} />
            <p className="mt-4 font-display text-3xl font-900 text-white">{s.value}</p>
            <p className="text-sm text-zinc-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex gap-2 border-b border-white/10">
        {["products", "orders"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-3 text-sm capitalize transition-colors ${tab === t ? "border-b-2 border-primary text-white" : "text-zinc-500 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-products-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-950 text-zinc-500">
              <tr>
                <th className="p-4 font-500">Card</th>
                <th className="p-4 font-500">Category</th>
                <th className="p-4 font-500">Price</th>
                <th className="p-4 font-500">Stock</th>
                <th className="p-4 font-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`admin-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt="" className="h-12 w-10 rounded object-cover" />
                      <span className="font-medium text-white">{p.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-zinc-400">{p.category}</td>
                  <td className="p-4 text-white">${p.price.toFixed(2)}</td>
                  <td className="p-4 text-zinc-400">{p.stock}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="rounded-lg border border-white/10 p-2 text-zinc-400 hover:text-white"><Pencil size={15} /></button>
                      <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="rounded-lg border border-white/10 p-2 text-zinc-400 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-orders-table">
          {orders.length === 0 ? (
            <p className="p-10 text-center text-zinc-500">No orders yet.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-950 text-zinc-500">
                <tr><th className="p-4 font-500">Items</th><th className="p-4 font-500">Amount</th><th className="p-4 font-500">Status</th><th className="p-4 font-500">Date</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-white/5">
                    <td className="p-4 text-zinc-300">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                    <td className="p-4 text-white">${(o.amount || 0).toFixed(2)}</td>
                    <td className="p-4"><span className="rounded-full bg-emerald-500/15 px-2 py-1 text-xs text-emerald-400">{o.status}</span></td>
                    <td className="p-4 text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()} data-testid="product-form-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-800 text-white">{editId ? "Edit card" : "Add card"}</h2>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="mt-5 space-y-3">
              <input data-testid="form-name" required placeholder="Card name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" />
              <textarea data-testid="form-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" rows={3} />
              <input data-testid="form-image" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="form-price" type="number" step="0.01" required placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" />
                <input data-testid="form-stock" type="number" required placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                </select>
                <select data-testid="form-rarity" value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value })} className="rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary">
                  {RARITIES.map((r) => <option key={r} value={r} className="bg-zinc-900">{r}</option>)}
                </select>
              </div>
              <input data-testid="form-grade" placeholder="Grade (e.g. PSA 10, BGS 9.5) — leave blank if ungraded" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="w-full rounded-xl border border-white/10 bg-card px-4 py-2.5 text-white outline-none focus:border-primary" />
              <label className="flex items-center gap-2 text-sm text-zinc-400">
                <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-violet-500" />
                Feature on homepage
              </label>
              <button type="submit" disabled={saving} data-testid="form-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={18} /> : editId ? "Save changes" : "Add card"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
